import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { ToggleButton } from "../HeadingToggle";
import { Icon } from "../Icon";
import { Tooltip } from "../Tooltip";
import type { CodeEditorHandle } from "./index";
import { SearchBar } from "./SearchBar";
import { TableSearchBar } from "./TableSearchBar";

type Registration = {
  registerEditor: (handle: CodeEditorHandle | null) => void;
  registerFilter: (active: boolean) => void;
  reportMatches: (count: number) => void;
};

const Context = createContext<Registration | null>(null);
const FilterQueryContext = createContext<string>("");

export function useCodeEditorSearchContext() {
  return useContext(Context);
}

export function useCodeEditorFilterQuery() {
  return useContext(FilterQueryContext);
}

export type CodeEditorSearch = {
  Provider: (props: { children: ReactNode }) => React.JSX.Element;
  toggleButton: ReactNode;
  subheader: ReactNode;
};

// resetKey closes the search when it changes (e.g. the selected step view), so
// search survives the in-place code/table toggle but not switching views.
export function useCodeEditorSearch(resetKey?: unknown): CodeEditorSearch {
  const [editor, setEditor] = useState<CodeEditorHandle | null>(null);
  const [filterActive, setFilterActive] = useState(false);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [matchCount, setMatchCount] = useState(0);

  const registration = useMemo<Registration>(
    () => ({
      registerEditor: setEditor,
      registerFilter: setFilterActive,
      reportMatches: setMatchCount,
    }),
    []
  );

  // Read live inside the (stable) Provider so filter changes reach the table
  // without recreating the Provider and remounting its subtree.
  const queryRef = useRef(query);
  queryRef.current = query;

  useEffect(() => {
    if (!editor && !filterActive) return;
    const handler = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || !e.shiftKey || e.altKey) return;
      if (e.key !== "f" && e.key !== "F") return;
      e.preventDefault();
      setOpen(!open);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [editor, filterActive, open]);

  // Drive the code editor's hidden search panel from `open` so the search
  // survives toggling between the code and table views.
  useEffect(() => {
    if (!editor) return;
    if (open) editor.openSearch();
    else editor.closeSearch();
  }, [editor, open]);

  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  useEffect(() => {
    setOpen(false);
  }, [resetKey]);

  const Provider = useCallback(
    ({ children }: { children: ReactNode }) => (
      <Context.Provider value={registration}>
        <FilterQueryContext.Provider value={queryRef.current}>
          {children}
        </FilterQueryContext.Provider>
      </Context.Provider>
    ),
    [registration]
  );

  const toggleButton =
    editor || filterActive ? (
      <Tooltip
        content={
          <>
            {open ? "Hide find" : "Find"}{" "}
            <span
              style={{
                fontSize: 10,
                color: "var(--tooltip-fg-muted)",
                marginLeft: 4,
              }}
            >
              ⌘⇧F
            </span>
          </>
        }
      >
        <ToggleButton
          onMouseDown={(e) => e.preventDefault()}
          onPress={() => setOpen(!open)}
          aria-pressed={open}
        >
          <Icon name="text-search" size={12} />
        </ToggleButton>
      </Tooltip>
    ) : null;

  const subheader = !open ? null : editor ? (
    <SearchBar
      view={editor.getView()}
      query={query}
      onChange={setQuery}
      onClose={() => setOpen(false)}
    />
  ) : filterActive ? (
    <TableSearchBar
      query={query}
      matchCount={matchCount}
      onChange={setQuery}
      onClose={() => setOpen(false)}
    />
  ) : null;

  return { Provider, toggleButton, subheader };
}
